import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Notifications,
  Person,
  Send,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const Profile: React.FC = () => {
  const { user } = useAuth();

  // ---- notifications state -----------------------------------------------
  const [haUrl, setHaUrl] = useState('');
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');
  const [notifErr, setNotifErr] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  // Load current values from profile endpoint
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/profile`, { headers: authHeaders() });
        const j = await r.json();
        if (r.ok && j.success && j.user) {
          setHaUrl(j.user.haWebhookUrl || '');
          setNotifEnabled(j.user.notificationsEnabled || false);
        }
      } catch (err) {
        console.error('Profile fetch error:', err);
      }
    })();
  }, []);

  const handleSaveNotifications = async () => {
    setNotifLoading(true);
    setNotifMsg('');
    setNotifErr('');
    try {
      const r = await fetch(`${API_BASE}/api/profile/notifications`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          haWebhookUrl: haUrl,
          notificationsEnabled: notifEnabled,
        }),
      });
      const j = await r.json();
      if (r.ok && j.success) {
        setNotifMsg('Notification settings saved');
      } else {
        setNotifErr(j.detail || j.message || 'Save failed');
      }
    } catch (err) {
      setNotifErr('Network error');
    } finally {
      setNotifLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setTestLoading(true);
    setNotifMsg('');
    setNotifErr('');
    try {
      const r = await fetch(`${API_BASE}/api/profile/notifications/test`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const j = await r.json();
      if (r.ok && j.success) {
        setNotifMsg('Test notification sent!');
      } else {
        setNotifErr(j.detail || j.message || 'Test failed');
      }
    } catch (err) {
      setNotifErr('Network error');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Account info */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Account
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user?.name || '—'}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {user?.email || '—'}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Account changes are managed via the admin CLI (scripts/create_user.py).
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Notifications */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Notifications sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notifications
                </Typography>
              </Box>

              {notifMsg && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotifMsg('')}>
                  {notifMsg}
                </Alert>
              )}
              {notifErr && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setNotifErr('')}>
                  {notifErr}
                </Alert>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1">Enable notifications</Typography>
                <Switch
                  checked={notifEnabled}
                  onChange={(e) => setNotifEnabled(e.target.checked)}
                />
              </Box>

              <TextField
                fullWidth
                label="Home Assistant Webhook URL"
                value={haUrl}
                onChange={(e) => setHaUrl(e.target.value)}
                placeholder="https://your-ha-instance.duckdns.org/api/webhook/..."
                margin="normal"
                helperText="The bot will POST events here (assignment, expiration, errors)."
                disabled={!notifEnabled}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveNotifications}
                  disabled={notifLoading}
                  startIcon={notifLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {notifLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleTestNotification}
                  disabled={testLoading || !notifEnabled || !haUrl}
                  startIcon={testLoading ? <CircularProgress size={16} /> : <Send />}
                >
                  {testLoading ? 'Sending...' : 'Send Test'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
